import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import FormConfigurationEditor from "../../components/FormConfigurationEditor";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import event from "../../services/event";
import queryDefault from "../../utils/queryDefault";
import cookie from "../../utils/useCookie";

// Payment method options
const PAYMENT_METHODS = [
  "CREDIT_CARD", "BCA", "BNI", "BSI", "BRI", "MANDIRI", "PERMATA",
  "SAHABAT_SAMPOERNA", "BNC", "ALFAMART", "INDOMARET", "OVO",
  "DANA", "SHOPEEPAY", "LINKAJA", "JENIUSPAY", "DD_BRI",
  "DD_BCA_KLIKPAY", "KREDIVO", "AKULAKU", "ATOME", "QRIS"
];

export default function EventEditPage() {
  const { id } = useParams();
  const notification = useNotification();
  const navigate = useNavigate();
  const user = JSON.parse(cookie.getCookie("USER") || "");
  const [binCodes, setBinCodes] = useState<string[]>([]);
  const [binInputValue, setBinInputValue] = useState("");
  const [binCodeError, setBinCodeError] = useState<string | null>(null);

  const schema = yup.object().shape({
    name: yup.string().required("Event name is a required field"),
    fee: yup.number().required("Fee price is a required field"),
    ppn: yup.number().required("PPN is a required field"),
    transaction_fee: yup.number().required("Transaction fee is a required field"),
    payment_methods: yup.array().min(1, "At least one payment method is required"),
    enabled: yup.boolean(),
  });

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm({
    defaultValues: {
      name: "",
      fee: "",
      username: user.username,
      ppn: "",
      photo: null,
      is_persen: false,
      description: "",
      location: "",
      include_tax: false,
      startDate: dayjs(),
      endDate: dayjs(),
      max_purchase: 0,
      show: false,
      payment_methods: [] as string[],
      enabled: true,
      invoice_duration: 0,
      checkout_time: 10,
      transaction_fee: "",
      transaction_fee_is_persen: false,
      meta_apikey: "",
      is_voucher_mandatory: false,
      meta_pixelid: "",
      uses_seating: false,
      is_checkout_v2: false,
      form_config: {
        fields: {
          name: { enabled: true, required: true, label: "Full Name", type: "text" },
          email: { enabled: true, required: true, label: "Email", type: "email" },
          phone: { enabled: true, required: true, label: "Phone", type: "tel" },
          gender: { enabled: false, required: false, label: "Gender", type: "select", options: ["Male", "Female", "Other"] },
          identity_number: { enabled: false, required: false, label: "Identity Number", type: "text" },
          date_of_birth: { enabled: false, required: false, label: "Date of Birth", type: "date" },
          address: { enabled: false, required: false, label: "Address", type: "textarea" },
          emergency_contact: { enabled: false, required: false, label: "Emergency Contact", type: "text" },
          company: { enabled: false, required: false, label: "Company/Organization", type: "text" },
          dietary_requirements: { enabled: false, required: false, label: "Dietary Requirements", type: "textarea" },
          special_needs: { enabled: false, required: false, label: "Special Needs", type: "textarea" },
        }
      },
    },
    resolver: yupResolver(schema),
  });

  // Check if CREDIT_CARD is selected
  const showBinCodeInput = form.watch("payment_methods")?.includes("CREDIT_CARD");

  // Handle bin code addition
  const handleAddBinCode = () => {
    // Validate if bin code is 6 or 8 digits
    if (!binInputValue.match(/^\d{6}$|^\d{8}$/)) {
      setBinCodeError("BIN code must be 6 or 8 digits");
      return;
    }

    // Add to bin codes array if not already present
    if (!binCodes.includes(binInputValue)) {
      setBinCodes([...binCodes, binInputValue]);
      setBinInputValue("");
      setBinCodeError(null);
    } else {
      setBinCodeError("BIN code already added");
    }
  };

  // Remove bin code
  const handleRemoveBinCode = (binToRemove: string) => {
    setBinCodes(binCodes.filter(bin => bin !== binToRemove));
  };

  const createEvent = useMutation("create_event", event.createEvent, {
    onSuccess: () => {
      navigate("/admin/event/");
      notification.onOpen({
        message: "Event was successfully created!",
        type: "success",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const updateEvent = useMutation("update-event", event.updateEvent, {
    onSuccess: () => {
      navigate("/admin/event/");
      notification.onOpen({
        message: "Event was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: any) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("username", values.username);
    formData.append("ppn", values.ppn);
    formData.append("fee", values.fee);
    formData.append("is_persen", values.is_persen);
    formData.append("transaction_fee", values.transaction_fee);
    formData.append("transaction_fee_is_persen", values.transaction_fee_is_persen);
    formData.append("show", values.show);
    formData.append("description", values.description);
    formData.append("location", values.location);
    formData.append("include_tax", values.include_tax);
    formData.append("is_voucher_mandatory", values.is_voucher_mandatory);
    formData.append("meta_apikey", values.meta_apikey);
    formData.append("meta_pixelid", values.meta_pixelid);
    formData.append(
      "start_date",
      dayjs(values.startDate).format("YYYY-MM-DD HH:mm")
    );
    formData.append(
      "end_date",
      dayjs(values.endDate).format("YYYY-MM-DD HH:mm")
    );
    formData.append("max_purchase", values.max_purchase);
    formData.append("enabled", values.enabled);

    // Convert minutes to seconds for invoice_duration
    const invoiceDurationSeconds = (values.invoice_duration || 1440) * 60;
    formData.append("invoice_duration", invoiceDurationSeconds.toString());

    // Add checkout_time in minutes
    formData.append("checkout_time", (values.checkout_time || 10).toString());

    // Add payment methods as comma-separated string
    formData.append("payment_method", values.payment_methods.join(","));

    // Add bin codes if CREDIT_CARD is selected
    if (values.payment_methods.includes("CREDIT_CARD") && binCodes.length > 0) {
      formData.append("bin_code", binCodes.join(","));
    }

    // Add form configuration as JSON string
    if (values.form_config) {
      formData.append("form_config", JSON.stringify(values.form_config));
    }

    // Add uses_seating
    formData.append("uses_seating", values.uses_seating);
    formData.append("is_checkout_v2", values.is_checkout_v2);

    if (values.photo && values.photo[0]) {
      formData.append("photo", values.photo[0]);
    }

    if (id) {
      formData.append("id", id);
      updateEvent.mutate(formData);
    } else {
      createEvent.mutate(formData);
    }
  };

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      event
        .viewEventDetail(id)
        .then(({ data }) => {
          form.setValue("name", data.name);
          form.setValue("fee", data.fee);
          form.setValue("ppn", data.ppn);
          form.setValue("location", data.location);
          form.setValue("is_persen", data.is_persen);
          form.setValue("include_tax", data.include_tax);
          form.setValue("show", data.show);
          form.setValue("description", data.description);
          form.setValue("name", data.name);
          form.setValue("max_purchase", data.max_purchase);
          form.setValue("startDate", dayjs(data.start_date));
          form.setValue("endDate", dayjs(data.end_date));
          form.setValue("invoice_duration", data.invoice_duration > 0 ? data.invoice_duration / 60 : 0);
          form.setValue("checkout_time", data.checkout_time || 10);
          form.setValue("transaction_fee", data.transaction_fee || 0);
          form.setValue("transaction_fee_is_persen", data.transaction_fee_is_persen || false);
          form.setValue("meta_apikey", data.meta_apikey || "");
          form.setValue("is_voucher_mandatory", data.is_voucher_mandatory || false);
          form.setValue("meta_pixelid", data.meta_pixelid || "");
          form.setValue("uses_seating", data.uses_seating || false);
          form.setValue("is_checkout_v2", data.is_checkout_v2 || false);
          // Set payment methods if they exist
          if (data.payment_method) {
            const methods = data.payment_method.split(',');
            form.setValue("payment_methods", methods);

            // Check if CREDIT_CARD is selected and bin_code exists
            if (methods.includes("CREDIT_CARD") && data.bin_code) {
              setBinCodes(data.bin_code.split(','));
            }
          }
          // Set enabled if present
          if (typeof data.enabled !== 'undefined') {
            form.setValue("enabled", data.enabled);
          }

          // Set form configuration if present
          if (data.form_config) {
            try {
              const formConfig = typeof data.form_config === 'string'
                ? JSON.parse(data.form_config)
                : data.form_config;
              form.setValue("form_config", formConfig);
            } catch (error) {
              console.error("Error parsing form_config:", error);
              // Keep default form config if parsing fails
            }
          }
        })
        .catch(() => {
          notification.onOpen({
            message: "Error fetching event",
            type: "error",
            position: "top",
          });
        })
        .finally(() => setIsLoading(false));
    }
  }, [id]);

  return (
    <MainLayout>
      <Helmet>
        <title>Edit Event</title>
      </Helmet>
      <Breadcrumb
        label={id ? "Edit Event" : "Create Event"}
        breadcrumbs={[
          { label: "Event", href: "/admin/event" },
          {
            label: id ? "Edit Event" : "Create Event",
            href: "/admin/events/create-event",
          },
        ]}
        href={"/admin/event"}
      />

      <ProtectedPermissionPage acceptPermissions={[3]}>
        {!isLoading ? (
          <Box sx={{ mt: 2 }}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {/* LEFT COLUMN - BASIC INFORMATION */}
                <Grid item xs={12} md={6}>
                  {/* Event Basic Information */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Event Information
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Event Name</FormLabel>
                      <Controller
                        name="name"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Location</FormLabel>
                      <Controller
                        name="location"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Start Date</FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          name="startDate"
                          control={form.control}
                          render={({ field: { ref, ...field }, fieldState }) => (
                            <DateTimePicker
                              {...field}
                              inputRef={ref}
                              value={field.value}
                              onChange={field.onChange}
                              format="YYYY-MM-DD HH:mm"
                              slotProps={{ textField: { variant: "outlined", size: "small" } }}
                              defaultValue={form.getValues().startDate}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>End Date</FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          name="endDate"
                          control={form.control}
                          render={({ field: { ref, ...field }, fieldState }) => (
                            <DateTimePicker
                              {...field}
                              inputRef={ref}
                              value={field.value}
                              onChange={field.onChange}
                              format="YYYY-MM-DD HH:mm"
                              slotProps={{ textField: { variant: "outlined", size: "small" } }}
                              defaultValue={form.getValues().endDate}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>

                    <FormControl fullWidth>
                      <FormLabel required>Description</FormLabel>
                      <Controller
                        name="description"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                            multiline
                            rows={4}
                          />
                        )}
                      />
                    </FormControl>
                  </Box>

                  {/* Pricing Information */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Pricing & Fees
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Admin Fee</FormLabel>
                      <Controller
                        name="fee"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormControlLabel
                        label="Admin Fee Is Percentage"
                        control={
                          <Controller
                            name="is_persen"
                            control={form.control}
                            render={({ field }) => (
                              <Checkbox
                                {...field}
                                checked={form.getValues().is_persen}
                              />
                            )}
                          />
                        }
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Transaction Fee</FormLabel>
                      <Controller
                        name="transaction_fee"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormControlLabel
                        label="Transaction Fee Is Percentage"
                        control={
                          <Controller
                            name="transaction_fee_is_persen"
                            control={form.control}
                            render={({ field }) => (
                              <Checkbox
                                {...field}
                                checked={form.getValues().transaction_fee_is_persen}
                              />
                            )}
                          />
                        }
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Tax (PPN)</FormLabel>
                      <Controller
                        name="ppn"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth>
                      <FormControlLabel
                        label="Include Tax"
                        control={
                          <Controller
                            name="include_tax"
                            control={form.control}
                            render={({ field }) => (
                              <Checkbox
                                {...field}
                                checked={form.getValues().include_tax}
                              />
                            )}
                          />
                        }
                      />
                    </FormControl>
                  </Box>
                </Grid>

                {/* RIGHT COLUMN - SETTINGS & CONFIGURATION */}
                <Grid item xs={12} md={6}>
                  {/* Purchase & Checkout Settings */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Checkout Settings
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Maximum Purchase per Customer</FormLabel>
                      <Controller
                        name="max_purchase"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Checkout Timeout (minutes)</FormLabel>
                      <Controller
                        name="checkout_time"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message || "Session duration in minutes (default: 10)"}
                            size="small"
                            inputProps={{ min: 1, max: 60 }}
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth>
                      <FormLabel required>Invoice Duration (minutes)</FormLabel>
                      <Controller
                        name="invoice_duration"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            type="number"
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message || "Payment expiration time in minutes (default: 600 = 10 mins)"}
                            size="small"
                            inputProps={{ min: 1, max: 10080 }}
                          />
                        )}
                      />
                    </FormControl>
                  </Box>

                  {/* Payment Methods */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Payment Methods
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel required>Select Payment Methods</FormLabel>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const currentMethods = form.getValues('payment_methods') || [];
                            if (currentMethods.length === PAYMENT_METHODS.length) {
                              form.setValue('payment_methods', []);
                            } else {
                              form.setValue('payment_methods', [...PAYMENT_METHODS]);
                            }
                          }}
                        >
                          {(form.watch('payment_methods') || []).length === PAYMENT_METHODS.length ? 'Deselect All' : 'Select All'}
                        </Button>
                        <Typography variant="caption" color="textSecondary">
                          {(form.watch('payment_methods') || []).length} of {PAYMENT_METHODS.length} selected
                        </Typography>
                      </Box>
                      <Controller
                        name="payment_methods"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <>
                            <Select
                              {...field}
                              multiple
                              input={<OutlinedInput />}
                              size="small"
                              error={Boolean(fieldState.error)}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                  ))}
                                </Box>
                              )}
                            >
                              {PAYMENT_METHODS.map((method) => (
                                <MenuItem key={method} value={method}>
                                  {method}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldState.error && (
                              <FormHelperText error>{fieldState.error.message}</FormHelperText>
                            )}
                          </>
                        )}
                      />
                    </FormControl>

                    {/* BIN Code Input - Only shown when CREDIT_CARD is selected */}
                    {showBinCodeInput && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel>BIN Codes (6 or 8 digits)</FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <TextField
                            value={binInputValue}
                            onChange={(e) => setBinInputValue(e.target.value)}
                            size="small"
                            placeholder="Enter BIN code"
                            error={Boolean(binCodeError)}
                            helperText={binCodeError}
                            sx={{ flexGrow: 1 }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleAddBinCode}
                            disabled={!binInputValue}
                            sx={{ mt: 1 }}
                          >
                            Add
                          </Button>
                        </Box>

                        {binCodes.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {binCodes.map((code) => (
                              <Chip
                                key={code}
                                label={code}
                                onDelete={() => handleRemoveBinCode(code)}
                              />
                            ))}
                          </Box>
                        )}
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                          Enter BIN codes and click Add. Each code must be 6 or 8 digits.
                        </Typography>
                      </FormControl>
                    )}
                  </Box>

                  {/* Status & Options */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Status & Options
                    </Typography>

                    <Stack spacing={2}>
                      <FormControl>
                        <FormControlLabel
                          label="Show Event"
                          control={
                            <Controller
                              name="show"
                              control={form.control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={form.getValues().show}
                                />
                              )}
                            />
                          }
                        />
                      </FormControl>

                      <FormControl>
                        <FormControlLabel
                          label="Enabled"
                          control={
                            <Controller
                              name="enabled"
                              control={form.control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={form.getValues().enabled}
                                />
                              )}
                            />
                          }
                        />
                      </FormControl>

                      <FormControl>
                        <FormControlLabel
                          label="Voucher Mandatory"
                          control={
                            <Controller
                              name="is_voucher_mandatory"
                              control={form.control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={form.getValues().is_voucher_mandatory}
                                />
                              )}
                            />
                          }
                        />
                      </FormControl>

                      <FormControl>
                        <FormControlLabel
                          label="Uses Seating"
                          control={
                            <Controller
                              name="uses_seating"
                              control={form.control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={form.getValues().uses_seating}
                                />
                              )}
                            />
                          }
                        />
                      </FormControl>

                      <FormControl>
                        <FormControlLabel
                          label="Checkout Use Seating"
                          control={
                            <Controller
                              name="is_checkout_v2"
                              control={form.control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={form.getValues().is_checkout_v2}
                                />
                              )}
                            />
                          }
                        />
                      </FormControl>
                    </Stack>
                  </Box>

                  {/* API Configuration */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      API Configuration
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <FormLabel>Meta Conversion API Key</FormLabel>
                      <Controller
                        name="meta_apikey"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>

                    <FormControl fullWidth>
                      <FormLabel>Meta Conversion Pixel ID</FormLabel>
                      <Controller
                        name="meta_pixelid"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={ref}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message}
                            size="small"
                          />
                        )}
                      />
                    </FormControl>
                  </Box>
                </Grid>

                {/* FULL WIDTH - FORM CONFIGURATION */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Checkout Form Configuration
                    </Typography>
                    <Controller
                      name="form_config"
                      control={form.control}
                      render={({ field }) => (
                        <FormConfigurationEditor
                          formConfig={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* FULL WIDTH - BANNER IMAGE */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Banner Image
                    </Typography>
                    <FormControl fullWidth>
                      <Controller
                        name="photo"
                        control={form.control}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <Box>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => field.onChange(e.target.files)}
                              ref={ref}
                              style={{ padding: '8px' }}
                            />
                            {form.formState.errors.photo && (
                              <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                {form.formState.errors.photo.message}
                              </Typography>
                            )}
                          </Box>
                        )}
                      />
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>

              {/* ACTION BUTTONS */}
              <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    loading={createEvent.isLoading}
                    sx={{ minWidth: 150 }}
                  >
                    SAVE
                  </LoadingButton>

                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    sx={{ minWidth: 150 }}
                  >
                    CANCEL
                  </Button>
                </Stack>
              </Box>
            </form>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        )}
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
